/* Fill Or Bust! 2026 promotion. Shopify's automatic discount sets prices. */
(() => {
  'use strict';
  if (window.FillOrBustPromotion) return;
  const config = window.FOB_PROMOTION_CONFIG;
  if (!config?.productId) return;
  const root = window.Shopify?.routes?.root || '/';
  const marker = '_fob_bonus';
  const value = '2026-buy3-get1';
  let queue = Promise.resolve();
  let latestCart;
  let warning = '';
  let pending = 0;
  const active = () => Date.now() >= Date.parse(config.start) && Date.now() < Date.parse(config.end);
  const eligible = item => String(item.product_id) === String(config.productId);
  const bonus = item => eligible(item) && item.properties?.[marker] === value;
  const paid = cart => cart.items.filter(item => eligible(item) && !bonus(item));
  const desired = cart => active() ? Math.floor(paid(cart).reduce((sum, item) => sum + item.quantity, 0) / 3) : 0;
  const gifts = cart => cart.items.filter(bonus);
  const giftCount = cart => gifts(cart).reduce((sum, item) => sum + item.quantity, 0);
  const discountValid = cart => {
    const expected = gifts(cart).reduce((sum, item) => sum + item.original_line_price, 0);
    const applied = cart.items.filter(eligible).reduce((sum, item) => sum +
      (item.line_level_discount_allocations || []).reduce((subtotal, allocation) =>
        subtotal + (allocation.discount_application?.title === config.discountTitle ? allocation.amount : 0), 0), 0);
    return expected > 0 && applied === expected;
  };
  async function readCart() {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not read cart');
    return response.json();
  }
  async function post(endpoint, data) {
    const response = await fetch(`${root}cart/${endpoint}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) throw new Error(result.description || 'Could not update promotional games');
    return result;
  }
  async function removeGifts(cart) {
    // Re-read after each change: Shopify can change line keys when discounts change.
    for (let attempt = 0; gifts(cart).length && attempt < 20; attempt++) {
      await post('change', { id: gifts(cart)[0].key, quantity: 0 });
      cart = await readCart();
    }
    if (gifts(cart).length) throw new Error('Could not remove promotional games');
    return cart;
  }
  async function reconcile() {
    warning = '';
    let cart = await readCart();
    const count = desired(cart);
    const purchased = paid(cart);
    if (!count) return latestCart = await removeGifts(cart);
    if (new Set(purchased.map(item => item.variant_id)).size !== 1 || purchased.some(item => item.selling_plan_allocation)) {
      warning = 'The bonus offer could not be applied to this selection. Please contact us before checking out.';
      return latestCart = await removeGifts(cart);
    }
    const variantId = purchased[0].variant_id;
    try {
      if (gifts(cart).length !== 1 || gifts(cart)[0].variant_id !== variantId) {
        cart = await removeGifts(cart);
        await post('add', { items: [{ id: variantId, quantity: count, properties: { [marker]: value } }] });
        cart = await readCart();
      } else if (giftCount(cart) !== count) {
        await post('change', { id: gifts(cart)[0].key, quantity: count });
        cart = await readCart();
      }
      if (giftCount(cart) !== count || !discountValid(cart)) {
        cart = await removeGifts(cart);
        warning = 'Your purchased games are in the cart, but the free-game discount could not be confirmed. Please contact us before checking out.';
      }
    } catch (error) {
      console.error('Fill Or Bust promotion:', error);
      // The paid add/change already succeeded. Never retry that mutation automatically.
      cart = await removeGifts(await readCart());
      warning = 'Your purchased games are in the cart, but the bonus games could not be added. Please contact us before checking out.';
    }
    return latestCart = cart;
  }
  async function sections(ids) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh cart display');
    const result = await response.json();
    if (ids.some(id => !result[id])) throw new Error('A cart section could not be rendered');
    return result;
  }
  function requestedSections(body) {
    const list = body instanceof FormData ? body.get('sections') : JSON.parse(body || '{}').sections;
    return Array.isArray(list) ? list : String(list || '').split(',').filter(Boolean);
  }
  function paintStatus() {
    document.querySelectorAll('[data-fob-status]').forEach(element => {
      element.hidden = !warning && (!active() || !latestCart || !paid(latestCart).length);
      let message;
      if (warning) message = warning;
      else if (latestCart && giftCount(latestCart) && discountValid(latestCart)) {
        const purchased = paid(latestCart).reduce((sum, item) => sum + item.quantity, 0);
        const free = giftCount(latestCart);
        message = `Buy 3, get 1 FREE: ${purchased} purchased + ${free} free = ${purchased + free} games in your order. Offer ends December 31, 2026 at 11:59 PM Central.`;
      } else message = 'Buy 3 games, get 1 FREE. One free game for every 3 purchased. Offer ends December 31, 2026 at 11:59 PM Central.';
      if (element.textContent !== message) element.textContent = message;
    });
    if (!latestCart) return;
    const keys = new Set(gifts(latestCart).map(item => item.key));
    document.querySelectorAll('[data-quantity-line-key]').forEach(input => {
      if (!keys.has(input.dataset.quantityLineKey)) return;
      const row = input.closest('.cart-item');
      row?.querySelectorAll('quantity-input input, quantity-input button, cart-remove-button button').forEach(control => {
        if (!control.disabled) control.disabled = true;
      });
    });
  }
  function enqueue(task) {
    pending++;
    const result = queue.then(task);
    queue = result.catch(() => {}).finally(() => { pending--; setTimeout(paintStatus, 0); });
    return result;
  }
  async function mutate(kind, url, options) {
    let request = options;
    if (kind === 'change') {
      const data = JSON.parse(options.body);
      // A stable key survives bonus-line insertion and removal better than a line index.
      const input = document.querySelector(`#Drawer-quantity-${data.line}, #Quantity-${data.line}`);
      if (input?.dataset.quantityLineKey) {
        data.id = input.dataset.quantityLineKey;
        delete data.line;
        request = { ...options, body: JSON.stringify(data) };
      }
    }
    // Snapshot before queueing: another render can remove the originating input.
    const ids = requestedSections(request.body);
    return enqueue(async () => {
      const response = await fetch(url, request);
      const original = await response.json();
      if (!response.ok || original.status || original.errors) return new Response(JSON.stringify(original), { status: response.status });
      let result = original;
      try {
        const cart = await reconcile();
        result = kind === 'add' ? { ...original, sections: ids.length ? await sections(ids) : original.sections } :
          { ...cart, sections: ids.length ? await sections(ids) : original.sections };
      } catch (error) {
        console.error('Fill Or Bust promotion:', error);
        warning = 'Your cart changed, but the promotion could not be checked. Please reload this page before checking out. Do not add the games again.';
      }
      return new Response(JSON.stringify(result), { status: 200, headers: { 'Content-Type': 'application/json' } });
    });
  }
  async function refreshVisibleCart() {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const rendered = await sections(ids);
    const parsed = new DOMParser().parseFromString(rendered['cart-drawer'], 'text/html');
    // Keep the drawer's open/closed state and do not open it on page load.
    const current = document.querySelector('cart-drawer');
    const incoming = parsed.querySelector('cart-drawer');
    if (current && incoming) {
      current.classList.toggle('is-empty', latestCart.item_count === 0);
      current.querySelector('#CartDrawer').innerHTML = incoming.querySelector('#CartDrawer').innerHTML;
      current.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => current.close());
    }
    const icon = document.getElementById('cart-icon-bubble');
    if (icon) icon.innerHTML = new DOMParser().parseFromString(rendered['cart-icon-bubble'], 'text/html').querySelector('.shopify-section')?.innerHTML || rendered['cart-icon-bubble'];
    if (typeof publish === 'function') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fob-promotion', cartData: latestCart });
  }
  window.FillOrBustPromotion = {
    add: (url, options) => mutate('add', url, options),
    change: (url, options) => mutate('change', url, options),
  };
  document.addEventListener('click', event => {
    const checkout = event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueue(async () => {
      try {
        await reconcile();
        if (!warning && pending === 1) window.location.assign(`${root}checkout`);
        else await refreshVisibleCart();
      } catch (error) {
        warning = 'Please reload this page so we can check your promotion before checkout.';
        console.error(error);
      }
    });
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.submitter.click();
  }, true);
  const initialize = () => {
    const observer = new MutationObserver(paintStatus);
    // Only child changes: setting status text must not cause an observer loop.
    observer.observe(document.body, { childList: true, subtree: true });
    enqueue(async () => {
      try { await reconcile(); await refreshVisibleCart(); }
      catch (error) { warning = 'Please reload this page before checkout; the promotion could not be checked.'; console.error(error); }
    });
    // Expired offers are also reconciled when an old tab is revisited.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) enqueue(async () => {
        try { await reconcile(); await refreshVisibleCart(); }
        catch (error) { warning = 'Please reload this page before checkout; the promotion could not be checked.'; }
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
